import { trackStep } from './errorReporting';

const reportedOperations = new Set<string>();

/**
 * Records, once per page load for each kind of skip, that translation had moved
 * a node React expected, so telemetry shows whether the safety net below ever
 * runs for real visitors. Nothing here can throw: trackStep is best-effort.
 */
function noteHit(operation: string): void {
  if (reportedOperations.has(operation)) return;
  reportedOperations.add(operation);
  trackStep('dom-patch-hit', { operation });
}

/**
 * Chrome's page translation wraps every text node in <font> elements, so a node
 * React still holds is no longer a child of the element React expects. React's
 * next removeChild / insertBefore then throws NotFoundError and the screen goes
 * blank (known React issue facebook/react#11538; here it hit the login "Continue"
 * button, whose label is swapped for loading dots). This installs, once and on
 * import, the usual workaround: a node that is no longer where React thinks it
 * is gets skipped (remove) or appended (insert) instead of throwing, so the page
 * keeps working with whatever the translation left in place.
 */
function installTranslateSafeDom(): void {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      noteHit('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    const stale = referenceNode !== null && referenceNode.parentNode !== this;
    if (stale) noteHit('insertBefore');
    const reference = stale ? null : referenceNode;
    return originalInsertBefore.call(this, newNode, reference) as T;
  };
}

installTranslateSafeDom();
